import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import s from "./OchronaTresci.module.css";

/** Rozmiar jednego kafelka w pikselach — musi odpowiadać `.siatka` w arkuszu (22rem × 11rem przy 16 px). */
export const KAFELEK_SZEROKOSC = 352;
export const KAFELEK_WYSOKOSC = 176;
/** Górny limit kafelków. Ekran 4K wyszedłby na ~250 węzłów; tyle nakładki nie potrzebuje, a DOM tak. */
export const MAKS_KAFELKOW = 160;
/** Co ile odświeżać stempel czasu na znaku wodnym. */
export const OKRES_STEMPLA_MS = 60 * 1000;

/** Ile kafelków pokryje siatkę 180% × 180% ekranu (z zapasem na obrót). */
export const liczbaKafelkow = (szerokosc, wysokosc) => {
    const kolumny = Math.max(1, Math.ceil((Number(szerokosc) || 0) * 1.8 / KAFELEK_SZEROKOSC));
    const wiersze = Math.max(1, Math.ceil((Number(wysokosc) || 0) * 1.8 / KAFELEK_WYSOKOSC));
    return Math.min(MAKS_KAFELKOW, Math.max(12, kolumny * wiersze));
};

/** „19.09.2026, 14:32" — data i godzina patrzenia na ekran, czyli to, co pozwala umiejscowić zrzut w czasie. */
export const stempelCzasu = (data = new Date()) => {
    try {
        return data.toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });
    } catch {
        return data.toISOString().slice(0, 16).replace("T", " ");
    }
};

/**
 * ZNAK WODNY Z DANYMI OGLĄDAJĄCEGO — odpowiedź na pytanie właściciela o „czarny ekran po PrintScreenie".
 *
 * [polecenie właściciela 2026-09-19] „klikam printscreen i zobacz co się dzieje, aplikacja jest czarna,
 * możesz mi zrobić coś takiego w mPracowniku?"
 *
 * NIE — i nie da się. Czarny obraz u Netflixa robi DRM na ścieżce wideo (chroniony strumień wycina
 * kompozytor systemu), zwykła strona nie ma czego tak chronić, a klawisz PrintScreen obsługuje system,
 * nie przeglądarka, więc strona nie dostaje nawet zdarzenia. To jest to, co DZIAŁA zamiast tego: zrzut
 * nadal powstanie, ale będzie na nim napisane, kto go zrobił i kiedy. Podpis przychodzi z serwera
 * i dotyczy OSOBY PRZY KLAWIATURZE (także w trybie podglądu cudzego konta).
 *
 * Napis jest zwykłym tekstem w DOM-ie, a nie obrazem tła — dzięki temu maska nagrania (wersja anonimowa)
 * obejmuje go tak samo jak resztę ekranu i nie wypisze prawdziwego nazwiska na nagraniu.
 * `aria-hidden` — dla czytnika ekranu to setka powtórzeń tej samej frazy, czyli sam szum.
 */
const ZnakWodny = ({ podpis }) => {
    const [stempel, setStempel] = useState(() => stempelCzasu());
    const [rozmiar, setRozmiar] = useState(() => ({
        szerokosc: typeof window === "undefined" ? 1280 : window.innerWidth,
        wysokosc: typeof window === "undefined" ? 800 : window.innerHeight,
    }));

    useEffect(() => {
        const zegar = window.setInterval(() => setStempel(stempelCzasu()), OKRES_STEMPLA_MS);
        const naRozmiar = () => setRozmiar({ szerokosc: window.innerWidth, wysokosc: window.innerHeight });
        window.addEventListener("resize", naRozmiar);
        return () => {
            window.clearInterval(zegar);
            window.removeEventListener("resize", naRozmiar);
        };
    }, []);

    const kafelki = useMemo(
        () => liczbaKafelkow(rozmiar.szerokosc, rozmiar.wysokosc),
        [rozmiar.szerokosc, rozmiar.wysokosc],
    );

    const napis = `${String(podpis || "").trim()} · ${stempel}`.replace(/^ · /, "");
    if (!napis.trim() || typeof document === "undefined") return null;

    // Portal do <body>: nakładka ma być liczona względem OKNA, a `position: fixed` w środku elementu
    // z transformacją (animacje układu) liczyłoby się względem niego i przestałoby pokrywać ekran.
    return createPortal(
        <div className={s.warstwa} aria-hidden="true" data-znak-wodny="">
            <div className={s.siatka}>
                {Array.from({ length: kafelki }, (_, i) => (
                    <span key={i} className={s.kafelek}>{napis}</span>
                ))}
            </div>
        </div>,
        document.body,
    );
};

export default ZnakWodny;
